// REST API client for Yoobikwitus.
//
// Real endpoints wired:
//   POST /analyse/upload   — file upload
//   POST /analyse/url      — URL submission
//   GET  /history          — case history and dashboard data
//   GET  /report/:id       — single report
//   GET  /source/candidates/:id — Vision API results
//
// Mock retained for: flagCase, batch (no backend routes yet)

import {
  MOCK_CASES,
  MOCK_BATCHES,
  buildSignals,
  NEWS_IMAGES,
  scoreTier,
} from "@/lib/mockData";

export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://app.yoobikwitus.com/api";

// ─── Auth token ───────────────────────────────────────────────────────────────
function getToken() {
  try {
    const raw = localStorage.getItem("sb-trokiiuteplbdowlypdi-auth-token");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.access_token || "";
  } catch {
    return "";
  }
}

function authHeaders() {
  return { Authorization: `Bearer ${getToken()}` };
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const body = await res.json();
      msg = body.detail || body.message || msg;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json();
}

// ─── Friendly case ID display ─────────────────────────────────────────────────
export function formatCaseId(id = "") {
  if (/^case_\d+$/.test(id)) return id;
  const short = id.replace(/-/g, "").slice(0, 8).toUpperCase();
  return `case_${short}`;
}

// ─── Mock helpers ─────────────────────────────────────────────────────────────
const delay = (min = 1000, max = 2000) =>
  new Promise((r) => setTimeout(r, Math.floor(min + Math.random() * (max - min))));

const store = {
  cases: [...MOCK_CASES],
  batches: [...MOCK_BATCHES],
};

let caseCounter = MOCK_CASES.length;
function nextCaseId() {
  caseCounter += 1;
  return `case_${caseCounter.toString().padStart(4, "0")}`;
}
function generateScore(seed) {
  const buckets = [88, 76, 64, 52, 41, 29, 18, 71, 83, 35];
  return buckets[seed % buckets.length];
}

// ─── Dashboard — derived from real /history data ──────────────────────────────
export async function getDashboard() {
  const data = await apiFetch("/history?limit=100");
  const items = (data.items || []).map(normaliseHistoryItem);

  const today = new Date().toDateString();
  const analysesToday = items.filter(
    (c) => new Date(c.submittedAt).toDateString() === today,
  ).length;

  const flaggedImages = items.filter((c) => c.status === "flagged").length;

  const batchesInProgress = items.filter((c) => c.status === "processing").length;

  const recent = items.slice(0, 6);

  return {
    stats: { analysesToday, flaggedImages, batchesInProgress },
    recent,
  };
}

// ─── History — real endpoint ──────────────────────────────────────────────────
export async function listCases({
  search = "",
  from = null,
  to = null,
  minScore = 0,
  maxScore = 100,
  flaggedOnly = false,
} = {}) {
  const data = await apiFetch("/history?limit=100");
  let rows = (data.items || []).map(normaliseHistoryItem);

  if (search) {
    const q = search.toLowerCase();
    rows = rows.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.sourceDomain.toLowerCase().includes(q) ||
        c.fileName.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q),
    );
  }
  if (from) {
    const fromTs = new Date(from).getTime();
    rows = rows.filter((c) => new Date(c.submittedAt).getTime() >= fromTs);
  }
  if (to) {
    const toTs = new Date(to).getTime() + 24 * 3600 * 1000;
    rows = rows.filter((c) => new Date(c.submittedAt).getTime() <= toTs);
  }
  rows = rows.filter((c) => c.score >= minScore && c.score <= maxScore);
  if (flaggedOnly) rows = rows.filter((c) => c.flagged);
  return rows;
}

// ─── Single case / report ─────────────────────────────────────────────────────
export async function getCase(id) {
  // Try fetching as a report ID first, then fall back to history lookup
  try {
    const data = await apiFetch(`/report/${id}`);
    return normaliseReport(data);
  } catch {
    const history = await apiFetch("/history?limit=100");
    const item = (history.items || []).find((i) => String(i.id) === String(id));
    if (!item) throw new Error("Case not found");
    if (item.report_id) {
      const report = await apiFetch(`/report/${item.report_id}`);
      return normaliseReport(report, item);
    }
    return normaliseHistoryItem(item);
  }
}

export async function flagCase(id, flagged = true) {
  await delay(400, 800);
  const c = store.cases.find((x) => x.id === id);
  if (c) {
    c.flagged = flagged;
    c.status = flagged ? "flagged" : "complete";
    return c;
  }
  return { id, flagged, status: flagged ? "flagged" : "complete" };
}

// ─── Reverse image search ─────────────────────────────────────────────────────
export async function reverseImageSearch(caseId) {
  try {
    const data = await apiFetch(`/source/candidates/${caseId}`);
    return (Array.isArray(data) ? data : []).map((s) => ({
      id: s.id || s.url,
      thumbnail: s.thumbnail || s.page_url || "",
      sourceDomain: s.domain || s.sourceDomain || "",
      url: s.sourceUrl || s.url || "",
      similarity: typeof s.similarity === "number" ? Math.round(s.similarity) : s.score || 0,
      capturedAt: s.capturedAt || s.captured_at || new Date().toISOString(),
    }));
  } catch {
    return [];
  }
}

// ─── Submit single analysis ───────────────────────────────────────────────────
export async function submitSingleAnalysis({ url, file, fileName }) {
  if (file) {
    const fd = new FormData();
    fd.append("file", file, fileName || file.name);
    const res = await fetch(`${API_BASE_URL}/analyse/upload`, {
      method: "POST",
      headers: authHeaders(),
      body: fd,
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error(b.detail || b.message || `Submission failed (${res.status})`);
    }
    return normaliseAnalysisResponse(await res.json());
  } else {
    const res = await fetch(`${API_BASE_URL}/analyse/url`, {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ image_url: url }),
    });
    if (!res.ok) {
      const b = await res.json().catch(() => ({}));
      throw new Error(b.detail || b.message || `Submission failed (${res.status})`);
    }
    return normaliseAnalysisResponse(await res.json());
  }
}

// ─── Batch (mock until backend ready) ────────────────────────────────────────
export async function submitBatchAnalysis(items) {
  await delay(1600, 2400);
  const batchId = `batch_${(store.batches.length + 1).toString().padStart(3, "0")}`;
  const cases = items.map((it, idx) => {
    const id = nextCaseId();
    const score = generateScore(caseCounter + idx);
    return {
      id,
      title: it.fileName || `Batch item ${idx + 1}`,
      thumbnail: it.url || NEWS_IMAGES[(caseCounter + idx) % NEWS_IMAGES.length],
      sourceDomain: "pending-review",
      sourceUrl: "",
      submittedUrl: it.url || "",
      score,
      status: "complete",
      flagged: score < 40,
      submittedAt: new Date().toISOString(),
      fileName: it.fileName || `${id}.jpg`,
      signals: buildSignals(score),
      batchId,
    };
  });
  cases.forEach((c) => store.cases.unshift(c));
  const flagged = cases.filter((c) => c.flagged).length;
  store.batches.unshift({
    id: batchId,
    name: `Batch ${batchId}`,
    submittedAt: new Date().toISOString(),
    total: cases.length,
    complete: cases.length,
    flagged,
  });
  return { batchId, cases };
}

export async function listBatchCases(batchId) {
  await delay(700, 1100);
  return store.cases.filter((c) => c.batchId === batchId);
}

export async function listBatches() {
  await delay(600, 900);
  return [...store.batches];
}

// ─── Normalisers ──────────────────────────────────────────────────────────────

function normaliseAnalysisResponse(c) {
  if (!c) return c;
  const id = String(c.id);
  const fileName = c.file_name || c.fileName || "";
  const imageUrl = c.image_url || c.imageUrl || "";
  return {
    id,
    title: fileName || formatCaseId(id),
    fileName,
    thumbnail: imageUrl || "",
    submittedUrl: imageUrl,
    sourceDomain: "",
    sourceUrl: c.source_url || "",
    score: 0,
    status: c.status || "processing",
    flagged: false,
    submittedAt: c.created_at || new Date().toISOString(),
    signals: [],
    batchId: c.batch_id || null,
  };
}

function normaliseReport(r, analysisItem = null) {
  if (!r) return r;
  const id = analysisItem?.id || r.analysis_id || r.id;
  const score = r.overall_score ?? 0;

  const signals = (r.signals || []).map((s) => ({
    name: s.name,
    score: typeof s.score === "number" && s.score <= 1
      ? Math.round(s.score * 100)
      : Math.round(s.score),
    finding: s.detail || s.finding || "",
    data_found: s.data_found ?? false,
  }));

  const imageUrl = analysisItem?.image_url || "";
  return {
    id: String(id),
    title: imageUrl
      ? imageUrl.split("/").pop()
      : formatCaseId(String(id)),
    fileName: imageUrl.split("/").pop() || "",
    thumbnail: imageUrl || "",
    submittedUrl: imageUrl,
    sourceDomain: r.sourceDomain || null,
    sourceUrl: r.source_url || "",
    score,
    status: analysisItem?.status || "complete",
    flagged: false,
    submittedAt: r.created_at || analysisItem?.created_at || new Date().toISOString(),
    signals,
    batchId: null,
    reportId: String(r.id),
    metadata_completeness: r.metadata_completeness ?? null,
    hiveResult: r.hive_result ?? null,
    verdict: r.verdict ?? null,
  };
}

function normaliseHistoryItem(item) {
  if (!item) return item;
  const id = String(item.id);
  const imageUrl = item.image_url || "";
  return {
    id,
    title: imageUrl ? imageUrl.split("/").pop().split("?")[0] : formatCaseId(id),
    fileName: imageUrl.split("/").pop() || "",
    thumbnail: imageUrl || "",
    submittedUrl: imageUrl,
    sourceDomain: "",
    sourceUrl: "",
    score: item.overall_score ?? 0,
    status: item.status || "complete",
    flagged: item.status === "flagged",
    submittedAt: item.created_at || new Date().toISOString(),
    signals: [],
    batchId: null,
  };
}

export { scoreTier };

// ─── Account / billing / admin (real endpoints) ───────────────────────────────
export async function getAccount() {
  return apiFetch("/account/me");
}

export async function getBillingConfig() {
  return apiFetch("/billing/config");
}

// Returns { checkout_url } for a new subscription, or { changed: true } when an
// existing subscription was switched between Plus and Pro.
export async function startCheckout(tier) {
  return apiFetch("/billing/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ tier }),
  });
}

export async function openBillingPortal() {
  return apiFetch("/billing/portal", { method: "POST" });
}

export async function getAdminOverview() {
  return apiFetch("/admin/overview");
}

export async function resyncSendy() {
  return apiFetch("/admin/sendy/resync", { method: "POST" });
}

// True when an API error came from the free-tier daily quota gate (HTTP 429).
export function isQuotaError(err) {
  return /one check per day|used today's/i.test(err?.message || "");
}
